import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  fetchDrugProducts,
  deleteDrugEnhanced,
  unassignDrugFromProducts,
} from "../../../../redux/Thunk/Product/ProductThunks";
import toast from "react-hot-toast";

const DeleteDrugWarningModal = ({ isOpen, onClose, drug, onSuccess }) => {
  const dispatch = useDispatch();
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProductIIDs, setSelectedProductIIDs] = useState([]);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const loadAssignedProducts = useCallback(async () => {
    if (!drug?.id) return;
    setLoading(true);
    try {
      const res = await dispatch(fetchDrugProducts(drug.id)).unwrap();
      setAssignedProducts(res?.products || []);
    } catch (err) {
      console.error("Failed to load products for delete warning:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, drug?.id]);

  useEffect(() => {
    if (isOpen && drug?.id) {
      loadAssignedProducts();
      setSelectedProductIIDs([]);
    }
  }, [isOpen, drug?.id, loadAssignedProducts]);

  if (!isOpen || !drug) return null;

  const count = assignedProducts.length;

  const handleSafeDelete = async () => {
    setIsDeleting(true);
    try {
      await dispatch(
        deleteDrugEnhanced({ id: drug.id, unassignAll: false })
      ).unwrap();
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.error || "Failed to delete drug");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnassignAllAndDelete = async () => {
    if (
      !window.confirm(
        `Are you sure? This will remove "${drug.drugName}" from all ${count} assigned products and permanently delete the drug.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await dispatch(
        deleteDrugEnhanced({ id: drug.id, unassignAll: true })
      ).unwrap();
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.error || "Failed to delete drug");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnassignSelected = async () => {
    if (selectedProductIIDs.length === 0) return;
    setIsUnassigning(true);
    try {
      await dispatch(
        unassignDrugFromProducts({
          drugId: drug.id,
          productIIDs: selectedProductIIDs,
        })
      ).unwrap();
      setSelectedProductIIDs([]);
      await loadAssignedProducts();
      toast.success("Removed drug from selected products.");
    } catch (err) {
      toast.error("Failed to unassign selected products.");
    } finally {
      setIsUnassigning(false);
    }
  };

  const toggleSelectProduct = (iid) => {
    setSelectedProductIIDs((prev) =>
      prev.includes(iid) ? prev.filter((id) => id !== iid) : [...prev, iid]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProductIIDs.length === assignedProducts.length) {
      setSelectedProductIIDs([]);
    } else {
      setSelectedProductIIDs(assignedProducts.map((p) => p.IID));
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 transition-opacity">
      <div
        className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-xl flex flex-col max-h-[85vh] my-auto overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-50/50 to-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                Delete Drug: {drug.drugName}
              </h3>
              <p className="text-xs text-gray-500 font-medium">
                {count > 0
                  ? "Dependency check: Drug is assigned to catalog products"
                  : "Confirmation: Safe deletion ready"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <svg
                className="animate-spin h-6 w-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              <span className="text-xs text-gray-500 font-medium">
                Verifying product assignments...
              </span>
            </div>
          ) : count === 0 ? (
            /* Safe to Delete View */
            <div className="space-y-4 text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center mx-auto">
                <svg
                  className="w-7 h-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900">
                  No Assigned Products
                </h4>
                <p className="text-xs text-gray-500 max-w-md mx-auto mt-1 leading-relaxed">
                  Drug <span className="font-bold text-gray-800">"{drug.drugName}"</span> is not currently used by any active product. Deleting it is safe and will not affect your catalog or inventory.
                </p>
              </div>
            </div>
          ) : (
            /* Assigned Products Warning View */
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="text-xs text-amber-900 leading-relaxed">
                  <span className="font-bold">Cannot delete directly:</span>{" "}
                  This drug is assigned to{" "}
                  <span className="font-black text-amber-950">{count}</span>{" "}
                  product{count > 1 ? "s" : ""}. To preserve database consistency, you must remove its assignment from these products before deletion.
                </div>
              </div>

              {/* Product Checklist */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between text-xs font-bold text-gray-700">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={
                        assignedProducts.length > 0 &&
                        selectedProductIIDs.length === assignedProducts.length
                      }
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 rounded text-primary-600 focus:ring-primary-500 border-gray-300 cursor-pointer"
                    />
                    <span>Assigned Products ({count})</span>
                  </div>
                  {selectedProductIIDs.length > 0 && (
                    <button
                      type="button"
                      disabled={isUnassigning}
                      onClick={handleUnassignSelected}
                      className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[11px] font-bold shadow-2xs transition-colors cursor-pointer"
                    >
                      {isUnassigning
                        ? "Removing..."
                        : `Unassign Selected (${selectedProductIIDs.length})`}
                    </button>
                  )}
                </div>

                <div className="max-h-52 overflow-y-auto divide-y divide-gray-100 p-1">
                  {assignedProducts.map((p) => {
                    const isChecked = selectedProductIIDs.includes(p.IID);
                    return (
                      <div
                        key={p.IID}
                        className={`p-2.5 rounded-xl flex items-center justify-between gap-3 text-xs transition-colors ${
                          isChecked ? "bg-primary-50/50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelectProduct(p.IID)}
                            className="w-3.5 h-3.5 rounded text-primary-600 focus:ring-primary-500 border-gray-300 cursor-pointer"
                          />
                          <div className="min-w-0">
                            <span className="font-bold text-gray-900 truncate block">
                              {p.productName}
                            </span>
                            <span className="font-mono text-[10px] text-gray-500">
                              #{p.IID} • {p.manufacturer || "Unknown Mfg"}
                            </span>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-semibold rounded shrink-0">
                          {p.type || "Product"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Option: Remove from All & Delete */}
              <div className="pt-2">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleUnassignAllAndDelete}
                  className="w-full py-2.5 px-4 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Remove from ALL {count} products & Delete Drug
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 shrink-0">
          <button
            type="button"
            disabled={isDeleting}
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>

          {count === 0 ? (
            <button
              type="button"
              disabled={isDeleting}
              onClick={handleSafeDelete}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              {isDeleting ? "Deleting..." : "Confirm Delete"}
            </button>
          ) : (
            <span className="text-[11px] text-gray-400 font-semibold italic">
              Unassign all products to unlock delete
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteDrugWarningModal;
